import React, { useEffect, useState, useRef } from 'react';
import { Search, MoreVertical, Paperclip, Send, Star, Clock, CheckCircle2, AlertTriangle, Phone, Video, Image as ImageIcon, FileText, Smile, Ban, MessageSquare, Mail, Megaphone, Eye, Zap, Plus, Trash2, Edit3, Users, ChevronDown } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { VideoCallModal } from '../ui/VideoCallModal';
interface Message {
  id: number;
  senderId: number | string;
  text: string;
  time: string;
  isOwn: boolean;
}
interface ChatContact {
  id: number;
  name: string;
  type: 'user' | 'astrologer' | 'moderator';
  avatar: string;
  time: string;
  unread: number;
  status: 'online' | 'offline';
  isVip?: boolean;
  hasIssue?: boolean;
  email?: string;
  phone?: string;
  regDate?: string;
}
interface MonitorChat {
  id: number;
  userName: string;
  userAvatar: string;
  astrologerName: string;
  astrologerAvatar: string;
  status: 'active' | 'completed' | 'flagged';
  messagesCount: number;
  lastActivity: string;
  messages: {
    sender: 'user' | 'astrologer';
    text: string;
    time: string;
  }[];
}
interface AdminFullChatProps {
  success: (msg: string) => void;
}
export function AdminFullChat({
  success
}: AdminFullChatProps) {
  const [mainTab, setMainTab] = useState<'direct' | 'broadcast' | 'monitor'>('direct');
  // ===== DIRECT CHAT =====
  const [activeChatId, setActiveChatId] = useState<number | null>(1);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [chatFilter, setChatFilter] = useState('all');
  const [showTemplates, setShowTemplates] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callMode, setCallMode] = useState<'video' | 'audio'>('video');
  const [callParticipant, setCallParticipant] = useState({
    name: '',
    initials: ''
  });
  // Real UI state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState<Set<number>>(new Set());
  const [spamUsers, setSpamUsers] = useState<Set<number>>(new Set());
  const [rightPanelTab, setRightPanelTab] = useState<'info' | 'orders' | null>(null);
  const [monitorIntervened, setMonitorIntervened] = useState<Set<number>>(new Set());
  const [monitorBlocked, setMonitorBlocked] = useState<Set<number>>(new Set());
  const [monitorExtraMessages, setMonitorExtraMessages] = useState<Record<number, {
    sender: 'user' | 'astrologer' | 'admin';
    text: string;
    time: string;
  }[]>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const commonEmojis = ['😊', '👍', '❤️', '🙏', '✨', '🔮', '⭐', '💫', '🌙', '☀️', '🎯', '✅', '❌', '🤔', '😂', '🥰', '💜', '🙌', '👏', '💡'];
  const handleFileAttach = (e: React.ChangeEvent<HTMLInputElement>, type: 'file' | 'image') => {
    const file = e.target.files?.[0];
    if (!file || !activeChatId) return;
    const newMsg: Message = {
      id: Date.now(),
      senderId: 'admin',
      text: type === 'image' ? `📷 ${file.name}` : `📎 ${file.name} (${(file.size / 1024).toFixed(1)} KB)`,
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      isOwn: true
    };
    setMessagesMap(prev => ({
      ...prev,
      [activeChatId]: [...(prev[activeChatId] || []), newMsg]
    }));
    e.target.value = '';
    success(type === 'image' ? 'Изображение отправлено' : 'Файл отправлен');
  };
  const insertEmoji = (emoji: string) => {
    setMessageText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };
  const handleSaveNote = () => {
    if (!noteText.trim() || !activeChatId) return;
    const chat = chats.find(c => c.id === activeChatId);
    success(`Заметка сохранена для ${chat?.name || 'пользователя'}`);
    setNoteText('');
    setShowNoteForm(false);
  };
  const toggleBlock = (userId: number) => {
    setBlockedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
        success('Пользователь разблокирован');
      } else {
        next.add(userId);
        success('Пользователь заблокирован');
      }
      return next;
    });
  };
  const toggleSpam = (userId: number) => {
    setSpamUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
        success('Метка спама снята');
      } else {
        next.add(userId);
        success('Помечен как спам');
      }
      return next;
    });
  };
  const chats: ChatContact[] = [{
    id: 1,
    name: 'Анна Ковалёва',
    type: 'user',
    avatar: 'АК',
    time: '10:42',
    unread: 0,
    status: 'online',
    isVip: true,
    email: 'anna@mail.ru',
    phone: '+7 (999) 123-45-67',
    regDate: '12 Окт 2024'
  }, {
    id: 2,
    name: 'Михаил Астров',
    type: 'astrologer',
    avatar: 'МА',
    time: '09:15',
    unread: 2,
    status: 'offline',
    email: 'astrov@mail.ru',
    phone: '+7 (916) 555-12-34',
    regDate: '5 Мар 2024'
  }, {
    id: 3,
    name: 'Елена Светлая',
    type: 'user',
    avatar: 'ЕС',
    time: 'Вчера',
    unread: 1,
    status: 'online',
    email: 'elena@mail.ru',
    regDate: '20 Авг 2024'
  }, {
    id: 4,
    name: 'Павел Орион',
    type: 'astrologer',
    avatar: 'ПО',
    time: 'Вчера',
    unread: 0,
    status: 'offline',
    isVip: true,
    email: 'orion@mail.ru',
    regDate: '1 Янв 2024'
  }, {
    id: 5,
    name: 'Дмитрий К.',
    type: 'user',
    avatar: 'ДК',
    time: 'Пн',
    unread: 0,
    status: 'offline',
    hasIssue: true,
    email: 'dmitry@mail.ru',
    regDate: '15 Сен 2024'
  }, {
    id: 6,
    name: 'Ольга Модератор',
    type: 'moderator',
    avatar: 'ОМ',
    time: '12:00',
    unread: 0,
    status: 'online',
    email: 'olga@astriqo.com',
    regDate: '1 Фев 2024'
  }, {
    id: 7,
    name: 'Мария Звёздная',
    type: 'astrologer',
    avatar: 'МЗ',
    time: 'Вт',
    unread: 0,
    status: 'online',
    email: 'maria@mail.ru',
    regDate: '10 Апр 2024'
  }, {
    id: 8,
    name: 'Игорь В.',
    type: 'user',
    avatar: 'ИВ',
    time: 'Вт',
    unread: 3,
    status: 'offline',
    email: 'igor@mail.ru',
    regDate: '22 Ноя 2024'
  }, {
    id: 9,
    name: 'Алексей Модератор',
    type: 'moderator',
    avatar: 'АМ',
    time: 'Ср',
    unread: 0,
    status: 'offline',
    email: 'alex@astriqo.com',
    regDate: '15 Мар 2024'
  }];
  const [messagesMap, setMessagesMap] = useState<Record<number, Message[]>>({
    1: [{
      id: 1,
      senderId: 1,
      text: 'Здравствуйте! У меня проблема с оплатой подписки Pro. Деньги списались, но статус не обновился.',
      time: '10:30',
      isOwn: false
    }, {
      id: 2,
      senderId: 'admin',
      text: 'Добрый день, Анна! Сейчас проверю вашу транзакцию.',
      time: '10:32',
      isOwn: true
    }, {
      id: 3,
      senderId: 'admin',
      text: 'Вижу оплату. Задержка на стороне платёжного шлюза. Я вручную активировал подписку — обновите страницу.',
      time: '10:35',
      isOwn: true
    }, {
      id: 4,
      senderId: 1,
      text: 'Да, теперь всё работает. Спасибо за помощь!',
      time: '10:42',
      isOwn: false
    }],
    2: [{
      id: 1,
      senderId: 2,
      text: 'Здравствуйте! Как мне обновить расписание на следующий месяц?',
      time: '09:10',
      isOwn: false
    }, {
      id: 2,
      senderId: 2,
      text: 'У меня не сохраняются слоты после редактирования.',
      time: '09:15',
      isOwn: false
    }],
    3: [{
      id: 1,
      senderId: 3,
      text: 'У меня не открывается отчёт по совместимости. Пишет ошибку 500.',
      time: 'Вчера',
      isOwn: false
    }],
    5: [{
      id: 1,
      senderId: 5,
      text: 'Можно ли вернуть деньги за отчёт? Он оказался не тем, что я ожидал.',
      time: 'Пн',
      isOwn: false
    }],
    6: [{
      id: 1,
      senderId: 6,
      text: 'Обратите внимание на пользователя ID 4022 — много жалоб за последнюю неделю.',
      time: '11:50',
      isOwn: false
    }, {
      id: 2,
      senderId: 'admin',
      text: 'Принято, беру в работу. Спасибо!',
      time: '12:00',
      isOwn: true
    }],
    8: [{
      id: 1,
      senderId: 8,
      text: 'Почему мой отзыв удалили? Я написал честное мнение!',
      time: 'Вт',
      isOwn: false
    }, {
      id: 2,
      senderId: 8,
      text: 'Это нарушение моих прав!',
      time: 'Вт',
      isOwn: false
    }, {
      id: 3,
      senderId: 8,
      text: 'Я буду жаловаться!',
      time: 'Вт',
      isOwn: false
    }]
  });
  const quickTemplates = ['Спасибо за обращение! Мы рассмотрим ваш вопрос.', 'Ваш вопрос передан специалисту.', 'Проблема решена. Если есть вопросы — пишите!', 'Пожалуйста, предоставьте номер заказа.', 'Мы проверили вашу оплату — всё в порядке.', 'Ваша заявка на верификацию принята.'];
  // ===== BROADCAST =====
  const [broadcastForm, setBroadcastForm] = useState({
    target: 'all',
    subject: '',
    message: '',
    chatChannel: true,
    emailChannel: true
  });
  const [broadcastHistory] = useState([{
    id: 1,
    subject: 'Новогодняя акция -30%',
    recipients: 12500,
    date: '25 Дек 2024',
    status: 'sent',
    openRate: '42%'
  }, {
    id: 2,
    subject: 'Обновление платформы v2.5',
    recipients: 12500,
    date: '20 Дек 2024',
    status: 'sent',
    openRate: '38%'
  }, {
    id: 3,
    subject: 'Новолуние в Козероге — специальный отчёт',
    recipients: 4500,
    date: '15 Дек 2024',
    status: 'sent',
    openRate: '55%'
  }, {
    id: 4,
    subject: 'Приглашение на вебинар',
    recipients: 120,
    date: '10 Дек 2024',
    status: 'scheduled',
    openRate: '—'
  }]);
  // ===== MONITOR =====
  const [monitorFilter, setMonitorFilter] = useState('all');
  const [activeMonitorId, setActiveMonitorId] = useState<number | null>(null);
  const monitorChats: MonitorChat[] = [{
    id: 101,
    userName: 'Анна К.',
    userAvatar: 'АК',
    astrologerName: 'Павел Орион',
    astrologerAvatar: 'ПО',
    status: 'active',
    messagesCount: 24,
    lastActivity: '2 мин назад',
    messages: [{
      sender: 'user',
      text: 'Здравствуйте! Хочу узнать про транзит Сатурна.',
      time: '14:00'
    }, {
      sender: 'astrologer',
      text: 'Добрый день! Сатурн сейчас проходит через ваш 7-й дом. Это влияет на партнёрские отношения.',
      time: '14:05'
    }, {
      sender: 'user',
      text: 'А как долго это продлится?',
      time: '14:08'
    }, {
      sender: 'astrologer',
      text: 'Примерно до марта 2025. Рекомендую обратить внимание на границы в отношениях.',
      time: '14:12'
    }]
  }, {
    id: 102,
    userName: 'Игорь В.',
    userAvatar: 'ИВ',
    astrologerName: 'Мария Звёздная',
    astrologerAvatar: 'МЗ',
    status: 'flagged',
    messagesCount: 8,
    lastActivity: '5 мин назад',
    messages: [{
      sender: 'user',
      text: 'Можно ваш номер телефона? Хочу обсудить вне платформы.',
      time: '13:30'
    }, {
      sender: 'astrologer',
      text: 'К сожалению, общение возможно только через платформу.',
      time: '13:35'
    }, {
      sender: 'user',
      text: 'Ну давайте хотя бы в телеграм перейдём, тут неудобно',
      time: '13:40'
    }]
  }, {
    id: 103,
    userName: 'Светлана М.',
    userAvatar: 'СМ',
    astrologerName: 'Михаил Астров',
    astrologerAvatar: 'МА',
    status: 'completed',
    messagesCount: 45,
    lastActivity: 'Вчера',
    messages: [{
      sender: 'user',
      text: 'Спасибо за консультацию! Всё было очень полезно.',
      time: '18:00'
    }, {
      sender: 'astrologer',
      text: 'Рад помочь! Если будут вопросы — обращайтесь.',
      time: '18:05'
    }]
  }, {
    id: 104,
    userName: 'Ольга Р.',
    userAvatar: 'ОР',
    astrologerName: 'Павел Орион',
    astrologerAvatar: 'ПО',
    status: 'active',
    messagesCount: 12,
    lastActivity: '15 мин назад',
    messages: [{
      sender: 'user',
      text: 'Мне нужен прогноз на февраль.',
      time: '11:00'
    }, {
      sender: 'astrologer',
      text: 'Конечно! Давайте уточним данные рождения.',
      time: '11:10'
    }]
  }];
  // ===== DIRECT CHAT LOGIC =====
  const activeChat = chats.find(c => c.id === activeChatId);
  const currentMessages = activeChatId ? messagesMap[activeChatId] || [] : [];
  const filteredChats = chats.filter(chat => {
    if (chatFilter !== 'all' && chat.type !== chatFilter) return false;
    if (searchQuery && !chat.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    if (mainTab === 'direct') scrollToBottom();
  }, [currentMessages, activeChatId, mainTab]);
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim() || !activeChatId) return;
    const newMsg: Message = {
      id: Date.now(),
      senderId: 'admin',
      text: messageText,
      time: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      }),
      isOwn: true
    };
    setMessagesMap(prev => ({
      ...prev,
      [activeChatId]: [...(prev[activeChatId] || []), newMsg]
    }));
    setMessageText('');
    setShowTemplates(false);
  };
  const getAvatarColor = (type: string) => {
    if (type === 'astrologer') return 'bg-purple-100 text-purple-700';
    if (type === 'moderator') return 'bg-emerald-100 text-emerald-700';
    return 'bg-blue-100 text-blue-700';
  };
  const getRoleLabel = (type: string) => {
    if (type === 'astrologer') return 'Астролог';
    if (type === 'moderator') return 'Модератор';
    return 'Клиент';
  };
  const getRoleBadgeVariant = (type: string): 'pro' | 'warning' | 'default' => {
    if (type === 'astrologer') return 'pro';
    if (type === 'moderator') return 'warning';
    return 'default';
  };
  const totalUnread = chats.reduce((sum, c) => sum + c.unread, 0);
  // ===== MONITOR LOGIC =====
  const activeMonitor = monitorChats.find(c => c.id === activeMonitorId);
  const filteredMonitor = monitorChats.filter(c => {
    if (monitorFilter === 'active') return c.status === 'active';
    if (monitorFilter === 'completed') return c.status === 'completed';
    if (monitorFilter === 'flagged') return c.status === 'flagged';
    return true;
  });
  // ===== TAB NAVIGATION =====
  const tabs = [{
    id: 'direct' as const,
    label: 'Диалоги',
    icon: <MessageSquare className="w-4 h-4" data-id="element-4011" />,
    badge: totalUnread
  }, {
    id: 'broadcast' as const,
    label: 'Рассылки',
    icon: <Megaphone className="w-4 h-4" data-id="element-4012" />
  }, {
    id: 'monitor' as const,
    label: 'Мониторинг',
    icon: <Eye className="w-4 h-4" data-id="element-4013" />,
    badge: monitorChats.filter(c => c.status === 'flagged').length
  }];
  return <div className="space-y-4" data-id="element-4014">
      <div data-id="element-4015">
        <h2 className="text-xl font-bold text-slate-900" data-id="element-4016">
          Коммуникационный центр
        </h2>
        <p className="text-sm text-slate-500" data-id="element-4017">
          Чаты, рассылки и мониторинг переписок
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200 pb-3" data-id="element-4018">
        {tabs.map(tab => <button key={tab.id} onClick={() => setMainTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${mainTab === tab.id ? 'bg-indigo-600 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-4019">
            {tab.icon}
            {tab.label}
            {tab.badge ? <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${mainTab === tab.id ? 'bg-white/20 text-white' : 'bg-rose-500 text-white'}`} data-id="element-4020">
                {tab.badge}
              </span> : null}
          </button>)}
      </div>

      {/* ===== TAB 1: DIRECT CHAT ===== */}
      {mainTab === 'direct' && <div className="h-[calc(100vh-14rem)] flex flex-col md:flex-row gap-4" data-id="element-4021">
          {/* Contact List */}
          <Card padding="none" className="w-full md:w-80 flex-shrink-0 flex flex-col border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-4022">
            <div className="p-4 border-b border-slate-100 space-y-3" data-id="element-4023">
              <div className="relative" data-id="element-4024">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-4025" />
                <input type="text" placeholder="Поиск диалогов..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-4026" />
              </div>
              <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide" data-id="element-4027">
                {[{
              id: 'all',
              label: 'Все'
            }, {
              id: 'user',
              label: 'Клиенты'
            }, {
              id: 'astrologer',
              label: 'Астрологи'
            }, {
              id: 'moderator',
              label: 'Модераторы'
            }].map(f => <button key={f.id} className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${chatFilter === f.id ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} onClick={() => setChatFilter(f.id)} data-id="element-4028">
                    {f.label}
                  </button>)}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto" data-id="element-4029">
              {filteredChats.map(chat => {
            const msgs = messagesMap[chat.id] || [];
            const lastMsg = msgs.length > 0 ? msgs[msgs.length - 1].text : 'Нет сообщений';
            return <div key={chat.id} onClick={() => setActiveChatId(chat.id)} className={`p-3 border-b border-slate-50 cursor-pointer transition-colors flex gap-3 ${activeChatId === chat.id ? 'bg-indigo-50/50' : 'hover:bg-slate-50'}`} data-id="element-4030">
                    <div className="relative flex-shrink-0" data-id="element-4031">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${getAvatarColor(chat.type)}`} data-id="element-4032">
                        {chat.avatar}
                      </div>
                      <div className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${chat.status === 'online' ? 'bg-emerald-500' : 'bg-slate-300'}`} data-id="element-4033" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-4034">
                      <div className="flex justify-between items-start mb-0.5" data-id="element-4035">
                        <h4 className="text-sm font-bold text-slate-900 truncate flex items-center gap-1" data-id="element-4036">
                          {chat.name}
                          {chat.isVip && <Star className="w-3 h-3 text-amber-500 fill-amber-500" data-id="element-4037" />}
                          {chat.hasIssue && <AlertTriangle className="w-3 h-3 text-rose-500" data-id="element-4038" />}
                        </h4>
                        <span className="text-[10px] text-slate-500 flex-shrink-0" data-id="element-4039">
                          {chat.time}
                        </span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-4040">
                        <p className={`text-xs truncate ${chat.unread > 0 ? 'font-medium text-slate-900' : 'text-slate-500'}`} data-id="element-4041">
                          {lastMsg.length > 40 ? lastMsg.slice(0, 40) + '…' : lastMsg}
                        </p>
                        {chat.unread > 0 && <span className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center flex-shrink-0 ml-2" data-id="element-4042">
                            {chat.unread}
                          </span>}
                      </div>
                    </div>
                  </div>;
          })}
            </div>
          </Card>

          {/* Chat Area */}
          {activeChat ? <Card padding="none" className="flex-1 flex flex-col border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-4043">
              {/* Header */}
              <div className="h-16 border-b border-slate-100 flex items-center justify-between px-6 bg-white flex-shrink-0" data-id="element-4044">
                <div className="flex items-center gap-3" data-id="element-4045">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${getAvatarColor(activeChat.type)}`} data-id="element-4046">
                    {activeChat.avatar}
                  </div>
                  <div data-id="element-4047">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-4048">
                      {activeChat.name}
                      <Badge variant={getRoleBadgeVariant(activeChat.type)} size="sm" data-id="element-4049">
                        {getRoleLabel(activeChat.type)}
                      </Badge>
                    </h3>
                    <div className="text-xs text-slate-500 flex items-center gap-1" data-id="element-4050">
                      <span className={`w-1.5 h-1.5 rounded-full ${activeChat.status === 'online' ? 'bg-emerald-500' : 'bg-slate-300'}`} data-id="element-4051" />
                      {activeChat.status === 'online' ? 'В сети' : 'Был(а) недавно'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-4052">
                  <Button variant="ghost" size="sm" icon={<Phone className="w-4 h-4" data-id="element-4054" />} className="hidden sm:flex" onClick={() => {
              setCallParticipant({
                name: activeChat.name,
                initials: activeChat.avatar
              });
              setCallMode('audio');
              setShowCallModal(true);
            }} data-id="element-4053" />
                  <Button variant="ghost" size="sm" icon={<Video className="w-4 h-4" data-id="element-4056" />} className="hidden sm:flex" onClick={() => {
              setCallParticipant({
                name: activeChat.name,
                initials: activeChat.avatar
              });
              setCallMode('video');
              setShowCallModal(true);
            }} data-id="element-4055" />
                  <div className="relative" data-id="element-4057">
                    <Button variant="ghost" size="sm" icon={<MoreVertical className="w-4 h-4" data-id="element-4059" />} onClick={() => setShowMoreMenu(!showMoreMenu)} data-id="element-4058" />
                    {showMoreMenu && <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-xl z-30 py-1 overflow-hidden" data-id="element-4060">
                        <button onClick={() => {
                  setShowNoteForm(true);
                  setShowMoreMenu(false);
                }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-4061">
                          <FileText className="w-4 h-4 text-slate-400" data-id="element-4062" />{' '}
                          Добавить заметку
                        </button>
                        <button onClick={() => {
                  if (activeChatId) toggleSpam(activeChatId);
                  setShowMoreMenu(false);
                }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-amber-700 hover:bg-amber-50 transition-colors" data-id="element-4063">
                          <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-4064" />
                          {activeChatId && spamUsers.has(activeChatId) ? 'Снять метку спама' : 'Пометить как спам'}
                        </button>
                        <button onClick={() => {
                  if (activeChatId) toggleBlock(activeChatId);
                  setShowMoreMenu(false);
                }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-rose-700 hover:bg-rose-50 transition-colors" data-id="element-4065">
                          <Ban className="w-4 h-4 text-rose-500" data-id="element-4066" />
                          {activeChatId && blockedUsers.has(activeChatId) ? 'Разблокировать' : 'Заблокировать'}
                        </button>
                        <div className="border-t border-slate-100 my-1" data-id="element-4067" />
                        <button onClick={() => {
                  if (activeChatId) {
                    setMessagesMap(prev => ({
                      ...prev,
                      [activeChatId]: []
                    }));
                    success('История очищена');
                  }
                  setShowMoreMenu(false);
                }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-500 hover:bg-slate-50 transition-colors" data-id="element-4068">
                          <Trash2 className="w-4 h-4 text-slate-400" data-id="element-4069" /> Очистить
                          историю
                        </button>
                      </div>}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 space-y-4" data-id="element-4070">
                <div className="text-center" data-id="element-4071">
                  <span className="text-xs font-medium text-slate-400 bg-slate-100 px-3 py-1 rounded-full" data-id="element-4072">
                    Сегодня
                  </span>
                </div>
                {currentMessages.map(msg => <div key={msg.id} className={`flex flex-col ${msg.isOwn ? 'items-end' : 'items-start'}`} data-id="element-4073">
                    <div className="flex items-end gap-2 max-w-[80%]" data-id="element-4074">
                      {!msg.isOwn && <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold mb-1 ${getAvatarColor(activeChat.type)}`} data-id="element-4075">
                          {activeChat.avatar}
                        </div>}
                      <div className={`p-3 rounded-2xl ${msg.isOwn ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'}`} data-id="element-4076">
                        <p className="text-sm whitespace-pre-wrap" data-id="element-4077">
                          {msg.text}
                        </p>
                      </div>
                    </div>
                    <div className={`text-[10px] text-slate-400 mt-1 ${msg.isOwn ? 'mr-1' : 'ml-9'}`} data-id="element-4078">
                      {msg.time}{' '}
                      {msg.isOwn && <CheckCircle2 className="w-3 h-3 inline-block ml-1 text-indigo-400" data-id="element-4079" />}
                    </div>
                  </div>)}
                {currentMessages.length === 0 && <div className="text-center text-slate-400 py-10" data-id="element-4080">
                    Нет сообщений. Напишите первым!
                  </div>}
                <div ref={messagesEndRef} data-id="element-4081" />
              </div>

              {/* Input */}
              <div className="p-4 bg-white border-t border-slate-100 flex-shrink-0" data-id="element-4082">
                {showTemplates && <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5" data-id="element-4083">
                    <div className="flex justify-between items-center mb-2" data-id="element-4084">
                      <span className="text-xs font-bold text-slate-700 flex items-center gap-1" data-id="element-4085">
                        <Zap className="w-3 h-3 text-amber-500" data-id="element-4086" /> Быстрые
                        ответы
                      </span>
                      <button onClick={() => setShowTemplates(false)} className="text-slate-400 hover:text-slate-600" data-id="element-4087">
                        <span className="text-xs" data-id="element-4088">✕</span>
                      </button>
                    </div>
                    {quickTemplates.map((t, i) => <button key={i} onClick={() => {
              setMessageText(t);
              setShowTemplates(false);
            }} className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg transition-colors" data-id="element-4089">
                        {t}
                      </button>)}
                  </div>}
                {/* Hidden file inputs */}
                <input ref={fileInputRef} type="file" className="hidden" onChange={e => handleFileAttach(e, 'file')} data-id="element-4090" />
                <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleFileAttach(e, 'image')} data-id="element-4091" />

                {/* Emoji Picker */}
                {showEmojiPicker && <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-4092">
                    <div className="flex justify-between items-center mb-2" data-id="element-4093">
                      <span className="text-xs font-bold text-slate-700" data-id="element-4094">
                        Эмодзи
                      </span>
                      <button onClick={() => setShowEmojiPicker(false)} className="text-slate-400 hover:text-slate-600 text-xs" data-id="element-4095">
                        ✕
                      </button>
                    </div>
                    <div className="grid grid-cols-10 gap-1" data-id="element-4096">
                      {commonEmojis.map(emoji => <button key={emoji} onClick={() => insertEmoji(emoji)} className="w-8 h-8 flex items-center justify-center text-lg hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-4097">
                          {emoji}
                        </button>)}
                    </div>
                  </div>}

                <form onSubmit={handleSendMessage} className="flex items-end gap-2" data-id="element-4098">
                  <div className="flex gap-1 pb-2" data-id="element-4099">
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="p-2 text-slate-400 hover:text-indigo-600 rounded-full hover:bg-slate-100 transition-colors" title="Прикрепить файл" data-id="element-4100">
                      <Paperclip className="w-5 h-5" data-id="element-4101" />
                    </button>
                    <button type="button" onClick={() => imageInputRef.current?.click()} className="p-2 text-slate-400 hover:text-indigo-600 rounded-full hover:bg-slate-100 transition-colors hidden sm:block" title="Отправить изображение" data-id="element-4102">
                      <ImageIcon className="w-5 h-5" data-id="element-4103" />
                    </button>
                    <button type="button" onClick={() => setShowTemplates(!showTemplates)} className={`p-2 rounded-full transition-colors ${showTemplates ? 'text-amber-600 bg-amber-50' : 'text-slate-400 hover:text-amber-600 hover:bg-slate-100'}`} title="Быстрые ответы" data-id="element-4104">
                      <Zap className="w-5 h-5" data-id="element-4105" />
                    </button>
                  </div>
                  <div className="flex-1 relative" data-id="element-4106">
                    <textarea placeholder="Введите сообщение..." className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-4 pr-10 py-3 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none max-h-32 min-h-[44px]" rows={1} value={messageText} onChange={e => {
                setMessageText(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }} data-id="element-4107" />
                    <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className={`absolute right-3 bottom-3 transition-colors ${showEmojiPicker ? 'text-amber-500' : 'text-slate-400 hover:text-amber-500'}`} title="Эмодзи" data-id="element-4108">
                      <Smile className="w-5 h-5" data-id="element-4109" />
                    </button>
                  </div>
                  <Button type="submit" variant="primary" className="h-11 w-11 rounded-xl flex items-center justify-center p-0 flex-shrink-0" disabled={!messageText.trim()} data-id="element-4110">
                    <Send className="w-5 h-5" data-id="element-4111" />
                  </Button>
                </form>
              </div>
            </Card> : <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 border border-slate-200 border-dashed rounded-xl" data-id="element-4112">
              <MessageSquare className="w-16 h-16 text-slate-300 mb-4" data-id="element-4113" />
              <h3 className="text-lg font-medium text-slate-900 mb-2" data-id="element-4114">
                Выберите диалог
              </h3>
              <p className="text-sm text-slate-500 max-w-sm text-center" data-id="element-4115">
                Выберите чат из списка слева.
              </p>
            </div>}

          {/* Right Info Panel */}
          {activeChat && <Card padding="none" className="w-72 flex-shrink-0 hidden xl:flex flex-col border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-4116">
              <div className="p-6 border-b border-slate-100 flex flex-col items-center text-center" data-id="element-4117">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold mb-4 shadow-sm ${getAvatarColor(activeChat.type)}`} data-id="element-4118">
                  {activeChat.avatar}
                </div>
                <h3 className="font-bold text-slate-900 text-lg" data-id="element-4119">
                  {activeChat.name}
                </h3>
                <p className="text-sm text-slate-500 mb-3" data-id="element-4120">
                  {getRoleLabel(activeChat.type)}
                </p>
                <div className="flex gap-2 w-full" data-id="element-4121">
                  <Button variant={rightPanelTab === 'info' ? 'primary' : 'outline'} size="sm" className="flex-1 text-xs" onClick={() => setRightPanelTab(rightPanelTab === 'info' ? null : 'info')} data-id="element-4122">
                    Профиль
                  </Button>
                  <Button variant={rightPanelTab === 'orders' ? 'primary' : 'outline'} size="sm" className="flex-1 text-xs" onClick={() => setRightPanelTab(rightPanelTab === 'orders' ? null : 'orders')} data-id="element-4123">
                    Заказы
                  </Button>
                </div>
              </div>
              <div className="p-5 space-y-5 overflow-y-auto flex-1" data-id="element-4124">
                {/* Profile info tab */}
                {rightPanelTab !== 'orders' && <div data-id="element-4125">
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-4126">
                      Информация
                    </h4>
                    <div className="space-y-3" data-id="element-4127">
                      {activeChat.email && <div className="flex items-start gap-3" data-id="element-4128">
                          <Mail className="w-4 h-4 text-slate-400 mt-0.5" data-id="element-4129" />
                          <div data-id="element-4130">
                            <div className="text-xs text-slate-500" data-id="element-4131">Email</div>
                            <div className="text-sm text-slate-900" data-id="element-4132">
                              {activeChat.email}
                            </div>
                          </div>
                        </div>}
                      {activeChat.phone && <div className="flex items-start gap-3" data-id="element-4133">
                          <Phone className="w-4 h-4 text-slate-400 mt-0.5" data-id="element-4134" />
                          <div data-id="element-4135">
                            <div className="text-xs text-slate-500" data-id="element-4136">
                              Телефон
                            </div>
                            <div className="text-sm text-slate-900" data-id="element-4137">
                              {activeChat.phone}
                            </div>
                          </div>
                        </div>}
                      {activeChat.regDate && <div className="flex items-start gap-3" data-id="element-4138">
                          <Clock className="w-4 h-4 text-slate-400 mt-0.5" data-id="element-4139" />
                          <div data-id="element-4140">
                            <div className="text-xs text-slate-500" data-id="element-4141">
                              Регистрация
                            </div>
                            <div className="text-sm text-slate-900" data-id="element-4142">
                              {activeChat.regDate}
                            </div>
                          </div>
                        </div>}
                    </div>
                  </div>}

                {/* Orders tab */}
                {rightPanelTab === 'orders' && <div data-id="element-4143">
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-4144">
                      Заказы
                    </h4>
                    <div className="space-y-2" data-id="element-4145">
                      {[{
                id: 'ORD-1042',
                service: 'Натальная карта',
                date: '15 Мар',
                status: 'completed',
                price: '3 500 ₽'
              }, {
                id: 'ORD-1038',
                service: 'Синастрия',
                date: '10 Мар',
                status: 'active',
                price: '5 000 ₽'
              }, {
                id: 'ORD-1025',
                service: 'Прогноз на месяц',
                date: '28 Фев',
                status: 'completed',
                price: '2 500 ₽'
              }].map(order => <div key={order.id} className="p-3 bg-slate-50 rounded-xl border border-slate-100" data-id="element-4146">
                          <div className="flex justify-between items-start mb-1" data-id="element-4147">
                            <span className="text-xs font-mono text-slate-500" data-id="element-4148">
                              {order.id}
                            </span>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${order.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-4149">
                              {order.status === 'completed' ? 'Выполнен' : 'Активный'}
                            </span>
                          </div>
                          <div className="text-sm font-medium text-slate-900" data-id="element-4150">
                            {order.service}
                          </div>
                          <div className="flex justify-between items-center mt-1" data-id="element-4151">
                            <span className="text-xs text-slate-500" data-id="element-4152">
                              {order.date}
                            </span>
                            <span className="text-xs font-medium text-slate-700" data-id="element-4153">
                              {order.price}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>}
                {/* Spam/Block status badges */}
                {activeChatId && (spamUsers.has(activeChatId) || blockedUsers.has(activeChatId)) && <div className="pt-4 border-t border-slate-100 space-y-2" data-id="element-4154">
                      {spamUsers.has(activeChatId) && <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-4155">
                          <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-4156" />
                          <span className="text-xs font-medium text-amber-700" data-id="element-4157">
                            Помечен как спам
                          </span>
                        </div>}
                      {blockedUsers.has(activeChatId) && <div className="flex items-center gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg" data-id="element-4158">
                          <Ban className="w-4 h-4 text-rose-500" data-id="element-4159" />
                          <span className="text-xs font-medium text-rose-700" data-id="element-4160">
                            Заблокирован
                          </span>
                        </div>}
                    </div>}

                <div className="pt-5 border-t border-slate-100" data-id="element-4161">
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3" data-id="element-4162">
                    Действия
                  </h4>

                  {/* Note form */}
                  {showNoteForm && <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-4163">
                      <textarea placeholder="Введите заметку..." value={noteText} onChange={e => setNoteText(e.target.value)} className="w-full bg-white border border-slate-200 rounded-lg p-2 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 resize-none min-h-[60px] mb-2" data-id="element-4164" />
                      <div className="flex gap-2" data-id="element-4165">
                        <button onClick={handleSaveNote} disabled={!noteText.trim()} className="flex-1 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50" data-id="element-4166">
                          Сохранить
                        </button>
                        <button onClick={() => {
                  setShowNoteForm(false);
                  setNoteText('');
                }} className="px-3 py-1.5 bg-slate-200 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-300 transition-colors" data-id="element-4167">
                          Отмена
                        </button>
                      </div>
                    </div>}

                  <div className="space-y-2" data-id="element-4168">
                    <button onClick={() => setShowNoteForm(!showNoteForm)} className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${showNoteForm ? 'text-indigo-700 bg-indigo-50' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-4169">
                      <FileText className="w-4 h-4 text-slate-400" data-id="element-4170" />{' '}
                      {showNoteForm ? 'Скрыть заметку' : 'Добавить заметку'}
                    </button>
                    <button onClick={() => {
                if (activeChatId) toggleSpam(activeChatId);
              }} className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${activeChatId && spamUsers.has(activeChatId) ? 'text-amber-800 bg-amber-100' : 'text-amber-700 hover:bg-amber-50'}`} data-id="element-4171">
                      <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-4172" />{' '}
                      {activeChatId && spamUsers.has(activeChatId) ? '✓ Снять метку спама' : 'Пометить как спам'}
                    </button>
                    <button onClick={() => {
                if (activeChatId) toggleBlock(activeChatId);
              }} className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${activeChatId && blockedUsers.has(activeChatId) ? 'text-rose-800 bg-rose-100' : 'text-rose-700 hover:bg-rose-50'}`} data-id="element-4173">
                      <Ban className="w-4 h-4 text-rose-500" data-id="element-4174" />{' '}
                      {activeChatId && blockedUsers.has(activeChatId) ? '✓ Разблокировать' : 'Заблокировать'}
                    </button>
                  </div>
                </div>
              </div>
            </Card>}
        </div>}

      {/* ===== TAB 2: BROADCAST ===== */}
      {mainTab === 'broadcast' && <div className="max-w-4xl mx-auto space-y-6" data-id="element-4175">
          <Card padding="md" className="border-slate-200 shadow-sm" data-id="element-4176">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100" data-id="element-4177">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-600" data-id="element-4178">
                <Megaphone className="w-5 h-5" data-id="element-4179" />
              </div>
              <div data-id="element-4180">
                <h2 className="text-lg font-bold text-slate-900" data-id="element-4181">
                  Новая рассылка
                </h2>
                <p className="text-sm text-slate-500" data-id="element-4182">
                  Отправка сообщений выбранной аудитории
                </p>
              </div>
            </div>
            <div className="space-y-5" data-id="element-4183">
              <div data-id="element-4184">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4185">
                  Аудитория
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-4186">
                  {[{
                id: 'all',
                label: 'Все пользователи',
                count: '12.5k'
              }, {
                id: 'vip',
                label: 'VIP клиенты',
                count: '450'
              }, {
                id: 'astrologers',
                label: 'Астрологи',
                count: '120'
              }, {
                id: 'inactive',
                label: 'Неактивные >30д',
                count: '3.2k'
              }].map(t => <div key={t.id} onClick={() => setBroadcastForm({
                ...broadcastForm,
                target: t.id
              })} className={`border rounded-xl p-3 cursor-pointer transition-colors ${broadcastForm.target === t.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300'}`} data-id="element-4187">
                      <div className={`font-medium text-sm mb-1 ${broadcastForm.target === t.id ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-4188">
                        {t.label}
                      </div>
                      <div className={`text-xs ${broadcastForm.target === t.id ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-4189">
                        ~{t.count}
                      </div>
                    </div>)}
                </div>
              </div>
              <div data-id="element-4190">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4191">
                  Каналы
                </label>
                <div className="flex gap-4" data-id="element-4192">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-4193">
                    <input type="checkbox" className="rounded text-indigo-600 focus:ring-indigo-500" checked={broadcastForm.chatChannel} onChange={e => setBroadcastForm({
                  ...broadcastForm,
                  chatChannel: e.target.checked
                })} data-id="element-4194" />
                    <span className="text-sm text-slate-700 flex items-center gap-1" data-id="element-4195">
                      <MessageSquare className="w-4 h-4 text-slate-400" data-id="element-4196" /> Чат
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-4197">
                    <input type="checkbox" className="rounded text-indigo-600 focus:ring-indigo-500" checked={broadcastForm.emailChannel} onChange={e => setBroadcastForm({
                  ...broadcastForm,
                  emailChannel: e.target.checked
                })} data-id="element-4198" />
                    <span className="text-sm text-slate-700 flex items-center gap-1" data-id="element-4199">
                      <Mail className="w-4 h-4 text-slate-400" data-id="element-4200" /> Email
                    </span>
                  </label>
                </div>
              </div>
              <Input label="Тема" placeholder="Например: Важное обновление платформы" value={broadcastForm.subject} onChange={e => setBroadcastForm({
            ...broadcastForm,
            subject: e.target.value
          })} data-id="element-4201" />
              <div data-id="element-4202">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4203">
                  Текст сообщения
                </label>
                <textarea className="w-full border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 min-h-[160px]" placeholder="Введите текст рассылки..." value={broadcastForm.message} onChange={e => setBroadcastForm({
              ...broadcastForm,
              message: e.target.value
            })} data-id="element-4204" />
              </div>
              <div className="pt-4 border-t border-slate-100 flex justify-end gap-3" data-id="element-4205">
                <Button variant="outline" onClick={() => success('Черновик сохранён')} data-id="element-4206">
                  Черновик
                </Button>
                <Button variant="primary" icon={<Send className="w-4 h-4" data-id="element-4208" />} onClick={() => {
              if (!broadcastForm.message) return success('Введите текст');
              success('Рассылка запущена!');
              setBroadcastForm({
                target: 'all',
                subject: '',
                message: '',
                chatChannel: true,
                emailChannel: true
              });
            }} data-id="element-4207">
                  Отправить
                </Button>
              </div>
            </div>
          </Card>

          <Card padding="md" className="border-slate-200 shadow-sm" data-id="element-4209">
            <h3 className="font-bold text-slate-900 mb-4" data-id="element-4210">История рассылок</h3>
            <div className="space-y-3" data-id="element-4211">
              {broadcastHistory.map(b => <div key={b.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-4212">
                  <div className="flex-1" data-id="element-4213">
                    <div className="font-medium text-sm text-slate-900" data-id="element-4214">
                      {b.subject}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-4215">
                      {b.date} · {b.recipients.toLocaleString()} получателей
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-4216">
                    <span className="text-xs text-slate-500" data-id="element-4217">
                      Open: {b.openRate}
                    </span>
                    <Badge variant={b.status === 'sent' ? 'success' : 'warning'} size="sm" data-id="element-4218">
                      {b.status === 'sent' ? 'Отправлено' : 'Запланировано'}
                    </Badge>
                  </div>
                </div>)}
            </div>
          </Card>
        </div>}

      {/* ===== TAB 3: MONITOR ===== */}
      {mainTab === 'monitor' && <div className="flex flex-col md:flex-row gap-4 h-[calc(100vh-14rem)]" data-id="element-4219">
          {/* Chat List */}
          <Card padding="none" className="w-full md:w-80 flex-shrink-0 flex flex-col border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-4220">
            <div className="p-4 border-b border-slate-100 space-y-3" data-id="element-4221">
              <div className="flex items-center gap-2" data-id="element-4222">
                <Eye className="w-5 h-5 text-rose-500" data-id="element-4223" />
                <h3 className="font-bold text-slate-900" data-id="element-4224">Мониторинг чатов</h3>
              </div>
              <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide" data-id="element-4225">
                {[{
              id: 'all',
              label: 'Все'
            }, {
              id: 'active',
              label: 'Активные'
            }, {
              id: 'flagged',
              label: '⚠ Подозрит.'
            }, {
              id: 'completed',
              label: 'Завершённые'
            }].map(f => <button key={f.id} className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${monitorFilter === f.id ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} onClick={() => setMonitorFilter(f.id)} data-id="element-4226">
                    {f.label}
                  </button>)}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto" data-id="element-4227">
              {filteredMonitor.map(chat => <div key={chat.id} onClick={() => setActiveMonitorId(chat.id)} className={`p-3 border-b border-slate-50 cursor-pointer transition-colors ${activeMonitorId === chat.id ? 'bg-rose-50/50' : 'hover:bg-slate-50'} ${chat.status === 'flagged' ? 'border-l-2 border-l-rose-500' : ''}`} data-id="element-4228">
                  <div className="flex justify-between items-start mb-2" data-id="element-4229">
                    <Badge variant={chat.status === 'active' ? 'success' : chat.status === 'flagged' ? 'error' : 'neutral'} size="sm" data-id="element-4230">
                      {chat.status === 'active' ? 'Активный' : chat.status === 'flagged' ? 'Подозрительный' : 'Завершён'}
                    </Badge>
                    <span className="text-[10px] text-slate-500" data-id="element-4231">
                      {chat.lastActivity}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm" data-id="element-4232">
                    <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold" data-id="element-4233">
                      {chat.userAvatar}
                    </div>
                    <span className="text-slate-400 text-xs" data-id="element-4234">↔</span>
                    <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[10px] font-bold" data-id="element-4235">
                      {chat.astrologerAvatar}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-4236">
                      <div className="text-xs font-medium text-slate-900 truncate" data-id="element-4237">
                        {chat.userName} — {chat.astrologerName}
                      </div>
                      <div className="text-[10px] text-slate-500" data-id="element-4238">
                        {chat.messagesCount} сообщ.
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </Card>

          {/* Monitor Chat View */}
          {activeMonitor ? <Card padding="none" className="flex-1 flex flex-col border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-4239">
              <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-amber-50/50 flex-shrink-0" data-id="element-4240">
                <div className="flex items-center gap-3" data-id="element-4241">
                  <Eye className="w-4 h-4 text-amber-600" data-id="element-4242" />
                  <span className="text-sm font-medium text-amber-800" data-id="element-4243">
                    Режим просмотра — вы наблюдаете за диалогом
                  </span>
                </div>
                <div className="flex gap-2" data-id="element-4244">
                  {activeMonitor.status === 'active' && <Button variant="outline" size="sm" className="text-xs border-indigo-200 text-indigo-600 hover:bg-indigo-50" icon={<Video className="w-3.5 h-3.5" data-id="element-4246" />} onClick={() => {
              setCallParticipant({
                name: `${activeMonitor.userName} — ${activeMonitor.astrologerName}`,
                initials: activeMonitor.userAvatar
              });
              setCallMode('video');
              setShowCallModal(true);
            }} data-id="element-4245">
                      Подключиться
                    </Button>}
                  {activeMonitor.status === 'flagged' && <Button variant="outline" size="sm" className={`text-xs ${monitorBlocked.has(activeMonitor.id) ? 'border-emerald-200 text-emerald-600 hover:bg-emerald-50' : 'border-rose-200 text-rose-600 hover:bg-rose-50'}`} onClick={() => {
              setMonitorBlocked(prev => {
                const next = new Set(prev);
                if (next.has(activeMonitor.id)) {
                  next.delete(activeMonitor.id);
                  success('Чат разблокирован');
                } else {
                  next.add(activeMonitor.id);
                  success('Чат заблокирован');
                }
                return next;
              });
            }} data-id="element-4247">
                      {monitorBlocked.has(activeMonitor.id) ? '✓ Разблокировать' : 'Заблокировать'}
                    </Button>}
                  <Button variant={monitorIntervened.has(activeMonitor.id) ? 'primary' : 'outline'} size="sm" className="text-xs" onClick={() => {
              if (!monitorIntervened.has(activeMonitor.id)) {
                setMonitorIntervened(prev => new Set(prev).add(activeMonitor.id));
                setMonitorExtraMessages(prev => ({
                  ...prev,
                  [activeMonitor.id]: [...(prev[activeMonitor.id] || []), {
                    sender: 'admin' as const,
                    text: '⚠️ Администратор подключился к чату. Пожалуйста, соблюдайте правила платформы.',
                    time: new Date().toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  }]
                }));
                success('Вы вмешались в чат — сообщение отправлено участникам');
              } else {
                success('Вы уже вмешались в этот чат');
              }
            }} data-id="element-4248">
                    {monitorIntervened.has(activeMonitor.id) ? '✓ Вмешались' : 'Вмешаться'}
                  </Button>
                </div>
              </div>
              <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-4 bg-white" data-id="element-4249">
                <div className="flex items-center gap-2" data-id="element-4250">
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold" data-id="element-4251">
                    {activeMonitor.userAvatar}
                  </div>
                  <div data-id="element-4252">
                    <div className="text-sm font-bold text-slate-900" data-id="element-4253">
                      {activeMonitor.userName}
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-4254">Клиент</div>
                  </div>
                </div>
                <span className="text-slate-300" data-id="element-4255">↔</span>
                <div className="flex items-center gap-2" data-id="element-4256">
                  <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold" data-id="element-4257">
                    {activeMonitor.astrologerAvatar}
                  </div>
                  <div data-id="element-4258">
                    <div className="text-sm font-bold text-slate-900" data-id="element-4259">
                      {activeMonitor.astrologerName}
                    </div>
                    <div className="text-[10px] text-slate-500" data-id="element-4260">Астролог</div>
                  </div>
                </div>
              </div>
              {/* Blocked banner */}
              {monitorBlocked.has(activeMonitor.id) && <div className="px-6 py-2 bg-rose-50 border-b border-rose-200 flex items-center gap-2" data-id="element-4261">
                  <Ban className="w-4 h-4 text-rose-500" data-id="element-4262" />
                  <span className="text-xs font-medium text-rose-700" data-id="element-4263">
                    Этот чат заблокирован администратором
                  </span>
                </div>}
              <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 space-y-4" data-id="element-4264">
                {[...activeMonitor.messages, ...(monitorExtraMessages[activeMonitor.id] || [])].map((msg, i) => <div key={i} className={`flex flex-col ${msg.sender === 'admin' ? 'items-center' : msg.sender === 'user' ? 'items-start' : 'items-end'}`} data-id="element-4265">
                    {msg.sender === 'admin' ? <div className="max-w-[90%] px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-center" data-id="element-4266">
                        <p className="text-sm text-amber-800 font-medium" data-id="element-4267">
                          {msg.text}
                        </p>
                        <span className="text-[10px] text-amber-500 mt-1 block" data-id="element-4268">
                          {msg.time} · Админ
                        </span>
                      </div> : <>
                        <div className="flex items-end gap-2 max-w-[80%]" data-id="element-4269">
                          {msg.sender === 'user' && <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold mb-1" data-id="element-4270">
                              {activeMonitor.userAvatar}
                            </div>}
                          <div className={`p-3 rounded-2xl ${msg.sender === 'user' ? 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm' : 'bg-purple-600 text-white rounded-br-sm'}`} data-id="element-4271">
                            <p className="text-sm" data-id="element-4272">{msg.text}</p>
                          </div>
                          {msg.sender === 'astrologer' && <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-[10px] font-bold mb-1" data-id="element-4273">
                              {activeMonitor.astrologerAvatar}
                            </div>}
                        </div>
                        <div className={`text-[10px] text-slate-400 mt-1 ${msg.sender === 'user' ? 'ml-9' : 'mr-9'}`} data-id="element-4274">
                          {msg.time}
                        </div>
                      </>}
                  </div>)}
              </div>
            </Card> : <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 border border-slate-200 border-dashed rounded-xl" data-id="element-4275">
              <Eye className="w-16 h-16 text-slate-300 mb-4" data-id="element-4276" />
              <h3 className="text-lg font-medium text-slate-900 mb-2" data-id="element-4277">
                Выберите чат для просмотра
              </h3>
              <p className="text-sm text-slate-500 max-w-sm text-center" data-id="element-4278">
                Выберите переписку из списка слева для мониторинга.
              </p>
            </div>}
        </div>}

      {/* Video Call Modal — Admin Enhanced */}
      <VideoCallModal isOpen={showCallModal} onClose={() => setShowCallModal(false)} mode={callMode} participantName={callParticipant.name} participantInitials={callParticipant.initials} onEnd={() => success('Звонок завершен. Запись сохранена.')} isAdmin={true} participants={activeChat ? [{
      name: activeChat.name,
      role: getRoleLabel(activeChat.type),
      initials: activeChat.avatar
    }] : activeMonitor ? [{
      name: activeMonitor.userName,
      role: 'Клиент',
      initials: activeMonitor.userAvatar
    }, {
      name: activeMonitor.astrologerName,
      role: 'Астролог',
      initials: activeMonitor.astrologerAvatar
    }] : undefined} data-id="element-4279" />
    </div>;
}